import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap, MapLocKeys } from '@cm/core/localization';

const dpRoot = 'Dashboard Report|Exam Voucher Verification';
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	feature: HubFeature.ComptiaCustomDashboardReports,
	portal: 'SP',
};

const htmlContent = `
<p>Press the Verify Voucher IDs button, then select a CSV file filled with Voucher ID information to receive a CSV file with certification verification for each matched candidate.</p>

<strong>To create the Comma Separated File (CSV) input verification file:</strong>

<p>The following column headers are recognized by the parser:</p>
<ul>
<li>Voucher ID (required)</li>
</ul>

<p><strong>Return CSV file:</strong><br />
After pressing the Verify Voucher IDs button, a CSV file will be returned with following columns:</p>

<ul>
<li>Voucher ID</li>
<li>Status</li>
<li>Candidate Name</li>
<li>Exam Name</li>
<li>Exam Code</li>
<li>Date Taken</li>
<li>Exam Grade</li>
<li>Candidate ID</li>
<li>No Show</li>
</ul>

<p>Voucher ID (the original Voucher ID supplied).<br />Status (field will contain one of the values as noted below):</p>
<ul>
<li>Not Found&mdash;The Voucher ID does not exist in our system</li>
<li>Found&mdash;The Voucher ID was found.</li>
<li>Found&mdash;The Voucher ID was found but no results. Exam incomplete (ex.: No Show)</li>
</ul>
<p>Candidate Name&mdash;The candidate's name if the voucher id was found.<br />
Exam Name&mdash;The name of the exam.<br />
Exam Code&mdash;The exam code.<br />
Date Taken&mdash;The date the exam was taken.<br />
Exam Grade&mdash;Pass or Fail - This will be blank if there is no indication.<br />
Client ID&mdash;CompTIA ID<br />
No Show&mdash;Found Voucher ID, but candidate was classified a No Show to the appointment.</p>
`;

export type SPExamVoucherVerificationLocNames =
	| 'SPExamVoucherVerificationPageTitle'
	| 'SPExamVoucherVerificationPageHeading'
	| 'SPExamVoucherVerificationSingleSection'
	| 'SPExamVoucherVerificationSingleButton'
	| 'SPExamVoucherVerificationSingleVoucherIdLabel'
	| 'SPExamVoucherVerificationMultipleVoucherIdRequiredError'
	| 'SPExamVoucherVerificationMultipleSection'
	| 'SPExamVoucherVerificationMultipleInstructions'
	| 'SPExamVoucherVerificationMultipleButton'
	| 'SPExamVoucherVerificationCsvUploadError'
	| 'SPExamVoucherVerificationSuccess';

export const spExamVoucherVerificationLocs: LocDefs<SPExamVoucherVerificationLocNames> = {
	SPExamVoucherVerificationPageTitle: {
		defaultValue: 'Exam Voucher Verification',
		description: 'Page title for the exam voucher verification page',
		displayPath: `${dpRoot}|Page Title`,
		displayType: 'Title',
		...sharedProperties,
	},
	SPExamVoucherVerificationPageHeading: {
		defaultValue: 'Exam Voucher Verification',
		description: 'Page Heading for the exam voucher verification page',
		displayPath: `${dpRoot}|Page Heading`,
		displayType: 'Title',
		...sharedProperties,
	},
	SPExamVoucherVerificationSingleSection: {
		defaultValue: 'Single Voucher ID Verification',
		description: 'Section title for the single voucher I/O verification section.',
		displayPath: `${dpRoot}|Single Voucher I/O Verification Section`,
		displayType: 'Title',
		...sharedProperties,
	},
	SPExamVoucherVerificationSingleVoucherIdLabel: {
		defaultValue: 'Voucher ID',
		description: 'Label for the voucher ID form field.',
		displayPath: `${dpRoot}|Single Voucher I/O Verification Voucher ID Label`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPExamVoucherVerificationMultipleVoucherIdRequiredError: {
		defaultValue: 'Voucher ID is required.',
		description: 'Error message for the voucher ID form field.',
		displayPath: `${dpRoot}|Multiple Voucher I/O Verification Voucher ID Required Error`,
		displayType: 'Error',
		...sharedProperties,
	},
	SPExamVoucherVerificationSingleButton: {
		defaultValue: 'Verify Voucher ID',
		description: 'Button text for the single voucher I/O verification section.',
		displayPath: `${dpRoot}|Single Voucher I/O Verification Button`,
		displayType: 'Button',
		...sharedProperties,
	},
	SPExamVoucherVerificationMultipleSection: {
		defaultValue: 'Exam voucher verification',
		description: 'Section title for the multiple voucher I/O verification section.',
		displayPath: `${dpRoot}|Multiple Voucher I/O Verification Section`,
		displayType: 'Title',
		...sharedProperties,
	},
	SPExamVoucherVerificationMultipleButton: {
		defaultValue: 'Verify Voucher IDs',
		description: 'Button text for the multiple voucher I/O verification section.',
		displayPath: `${dpRoot}|Multiple Voucher I/O Verification Button`,
		displayType: 'Button',
		...sharedProperties,
	},
	SPExamVoucherVerificationMultipleInstructions: {
		defaultValue: htmlContent,
		description:
			'Instructions for the multiple voucher IDs section of the voucher I/O verification page.',
		displayPath: `${dpRoot}|Multiple Voucher IDs Instructions`,
		displayType: 'Content',
		shared: false,
		htmlAllowed: true,
		feature: HubFeature.ComptiaCustomDashboardReports,
		portal: 'SP',
	},
	SPExamVoucherVerificationCsvUploadError: {
		defaultValue: 'Error parsing CSV file.',
		description: 'Error message for the CSV upload error.',
		displayPath: `${dpRoot}|CSV Upload Error`,
		displayType: 'Error',
		...sharedProperties,
	},
	SPExamVoucherVerificationSuccess: {
		defaultValue: 'Voucher(s) verified successfully. Your download should start shortly.',
		description: 'Success message when verifying voucher(s).',
		displayPath: `${dpRoot}|Verification Success`,
		displayType: 'Notification',
		...sharedProperties,
	},
};

export type SPExamVoucherVerificationLocType = LocMap<SPExamVoucherVerificationLocNames>;

export const EXAM_VOUCHER_VERIFICATION_LOC_KEYS = MapLocKeys<SPExamVoucherVerificationLocNames>(
	spExamVoucherVerificationLocs
);
